/**
 * English — the source of truth for the dictionary shape.
 *
 * `uk.ts` and `ru.ts` are typed against this object, so a missing or renamed key is a compile error
 * rather than a phrase that silently falls back to English in production. Anything with a number or a
 * name is a function, not a template glued together at the call site: word order and pluralisation
 * differ per language.
 */

export const en = {
  lang: { en: 'English', uk: 'Українська', ru: 'Русский', label: 'Language' },

  nav: {
    overview: 'Overview',
    posting: 'Posting',
    settings: 'Settings',
    dashboard: 'Dashboard',
    phones: 'Phones',
    workspace: 'Workspace',
    videos: 'Videos',
    distributions: 'Distributions',
    posts: 'Posts',
    sources: 'Sources',
    scenarios: 'Scenarios',
    machines: 'Machines',
    accounts: 'Accounts',
    directions: 'Directions',
    publications: 'Publications',
    links: 'Links',
    users: 'Users',
    myClaude: 'My Claude',
    signOut: 'Sign out',
    serviceToken: 'service token',
    signedInAs: (email: string) => `Signed in as ${email}`,
    withToken: 'Signed in with a service token',
    lightTheme: 'Light theme',
    darkTheme: 'Dark theme',
    groupWork: 'Work',
    groupSetup: 'Setup',
    collapse: 'Collapse the menu',
    expand: 'Expand the menu',
  },

  auth: {
    title: 'Ferma Hub',
    intro: 'Sign in with your work account to reach the machines and the phones on them.',
    email: 'Work e-mail',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    refused: 'That e-mail and password were not accepted.',
    notConfigured: 'The service has no sign-in configured yet — ask an administrator.',
    failed: (msg: string) => `could not sign in: ${msg}`,
    corporateOnly: 'Corporate accounts only (@mornhouse.com). Password accounts are created by an administrator.',
    googleRefused: 'Google did not confirm this sign-in. Try again.',
    googleWrongDomain: 'Sign in with your corporate Google account (@mornhouse.com).',
    usePassword: 'Sign in with a password instead',
  },

  devices: {
    notRecommended: (n: number) => `${n} post(s) out of For You`,
    title: 'Phones',
    onlineOf: (online: number, total: number) => `${online} of ${total} reachable`,
    search: 'Model, serial, machine, account…',
    all: 'all',
    free: 'free',
    mine: 'mine',
    refresh: 'Refresh',
    nothing: 'Nothing matches.',
    hint: 'A phone shows up here on its own as soon as it is plugged into a machine — nothing to register.',
    open: 'open →',
    mySession: 'your session',
    busyFor: (min: number) => `busy ${min} min`,
    busy: 'busy',
    offline: 'offline',
    error: (msg: string) => `error: ${msg}`,
    statusOnline: 'Online — ready to connect',
    statusBusy: 'Busy — someone else is holding it',
    posting: 'posting',
    statusPosting: 'Automatic posting holds this phone; nobody can take it',
    statusOffline: 'Offline — check the cable and the machine',
    adbTrouble: (state: string) => `adb: ${state}`,
    battery: (pct: number) => `${pct}%`,
    hot: (deg: number) => `${deg}°`,
    machineFilter: 'Machine',
    allMachines: 'all machines',
    directionFilter: 'Direction',
    allDirections: 'all directions',
    noDirection: 'no direction yet',
    noAccount: 'no TikTok account set',
  },

  serviceKeys: {
    title: 'Keys for other services',
    sub: 'A read-only key for another of our services, e.g. the equipment register. It reads, and changes nothing.',
    name: 'What will use it',
    namePh: 'Equipment register',
    scopes: 'What it may read',
    create: 'Issue key',
    shownOnce: 'The key is shown once — copy it before you leave the page.',
    issued: 'The key — visible only now',
    list: 'Issued keys',
    listSub: 'Revoke a key and the service using it is refused on its next request.',
    none: 'No keys issued yet.',
    by: (email: string) => `issued by ${email}`,
    used: (at: string) => `last used ${at}`,
    neverUsed: 'never used',
    revoke: 'Revoke',
    remove: 'Delete',
    live: 'active',
    revoked: 'revoked',
    copy: 'copy',
    confirmRevoke: 'Revoke the key? The service using it is refused from its next request.',
    confirmRemove: 'Delete the key? It is revoked too and will never work again.',
    scope: {
      'read:hubs': 'computers — which are connected and which phones are on each',
      'read:devices': 'phones — model, status, computer, TikTok account, who holds one',
    } as Record<string, string>,
  },
  myClaude: {
    title: 'Connect your own Claude',
    sub: 'Your Claude works here as you: it sees the machines and phones, takes a phone in your name and drives it, uploads videos and sources, creates and launches posts and distributions. Scenarios, phones, machines, users and other people’s content stay with you in the workspace.',
    connectTitle: 'Connect — no key to copy',
    connectSteps: [
      'In claude.ai, Claude Desktop or Claude Code add the connector with the address below — no key, no header.',
      'Press Connect. A Ferma Hub page opens: check it says Claude, press Allow.',
      'Done — your Claude works as you, and its key appears in the list below like any other.',
    ],
    connectNote: 'Claude Code: run the command below once, then type /mcp, pick ferma-hub and choose Authenticate — the browser opens the same Allow page.',
    manualTitle: 'Manual key — only for scripts',
    manualSub: 'A key acts as you exactly like the connector does. Prefer Connect: nothing to copy, and disconnecting is one click.',
    create: 'Issue my key',
    shownOnce: 'The key is shown once — copy it before leaving this page.',
    issued: 'Your key — shown only now',
    web: 'Claude in the browser with a key: Settings → Connectors → Add custom connector, URL above, request header Authorization = Bearer <your key>.',
    code: 'Claude Code with a key:',
    mine: 'My keys and connections',
    mineSub: 'Every Claude you connected is a row here. Revoke one and that Claude stops working at once.',
    none: 'Nothing connected yet.',
    revoke: 'Revoke',
    remove: 'Remove',
    live: 'live',
    revoked: 'revoked',
    copy: 'copy',
    confirmRevoke: 'Revoke this key? Your Claude stops working with it within a minute.',
    confirmRemove: 'Remove this key? It is revoked too and never works again.',
  },
  session: {
    openElsewhere: 'This phone is already open in another tab of yours.',
    showHere: 'Show it here',
    holdWarning: (s: number) => `Nothing done for a while — the phone frees itself in ${s} s.`,
    keep: 'Keep the phone',
    holdLeft: (min: number) => `${min} min left if idle`,
    holdLeftTitle: 'The phone is released after 10 minutes with no tap, key or install from you — any action resets it',
    actions: 'Phone actions',
    lapsed: 'The phone may have been released for inactivity.',
    record: 'Record scenario',
    recording: 'Recording… stop',
    recordHint: 'Recording: do the whole TikTok posting flow now. The replay starts from the screen you are on right now (Home or the open app). Taps are a little slower — the hub reads the screen before each one.',
    recordStopped: (n: number) => `${n} steps recorded`,
    recordEmpty: 'nothing was recorded',
    recordSaveTitle: 'Save the recorded scenario',
    recordName: 'Scenario name',
    recordTextWarning: 'The recording contains typed text. It will be typed verbatim on every phone — remove the caption step if that is not intended.',
    recordDiscard: 'Discard',
    expiredTitle: 'Your session has ended',
    expiredBody: 'Sessions are short on purpose. Nothing here works until you sign in again — press OK to go to the sign-in screen.',
    expiredOk: 'OK',
    pickTitle: 'Pick a phone on the left',
    pickBody:
      'A live screen opens here: tap, swipe with real flick speed, press and hold, hardware keys, typing from your keyboard, device audio, screenshots — and the phone’s log. This is where a pushed video is published to TikTok by hand.',
    pickNote: 'One phone is held by one person at a time — it has one screen, one hardware encoder and one input stream.',
    live: 'live screen',
    starting: 'starting up',
    disconnect: 'Disconnect',
    close: 'Close',
    disconnecting: 'Disconnecting…',
    busyBody: (who: string | null, forMin: number | null) =>
      `Someone else is holding this phone${who ? ` (${who})` : ''}${forMin ? `, for ${forMin} min now` : ''}. Nobody can take a phone from somebody: it frees itself when they stop or go idle — pick another phone.`,
    postingBody: (who: string | null) =>
      `Automatic posting is running on this phone${who ? ` (${who})` : ''} — it holds the phone until the posting ends; nobody can take it.`,
    preemptedByPost:
      'Automatic posting took this phone — it is a priority task. The phone comes back when the posting ends; nothing of yours is lost.',
    failed: 'the session did not come up',
    retry: 'Try again',
    endedOnServer: 'the session was ended on the server',
    hubStarting: 'the machine is bringing the stream up…',
    startupNote: (elapsed: number, expected: number) =>
      `${elapsed} s · usually about ${expected} s: the service asks the machine, which starts scrcpy and waits for control and screen geometry`,
    notReady: 'the screen is not ready yet',
    back: 'Back',
    home: 'Home',
    recents: 'Recents',
    wake: 'Wake',
    power: 'Power',
    volDown: 'Volume down (on the phone)',
    volUp: 'Volume up (on the phone)',
    swipeUp: 'Swipe up',
    swipeDown: 'Swipe down',
    screenshot: 'Screenshot',
    screenshotSaved: 'Screenshot saved',
    keyboard: 'Keyboard',
    keyboardOn: 'Keyboard: on',
    keyboardHint: 'Keystrokes go to the phone. Esc = Back. Turn it off to type on this page.',
    keyboardTitle: 'Type straight into the phone: letters go as text, Enter/Backspace/arrows as keys',
    typeHere: 'Type text on the device…',
    send: 'Send',
    gestureHint: 'Click = tap, drag = swipe with real speed, hold = long press.',
    sessionId: 'Session id',
    video: 'Video',
    quality: 'Quality',
    qualityTitle: 'Video bitrate. Resolution stays native — otherwise scrcpy drops touches',
    q3: '3 Mbit · normal',
    q5: '5 Mbit · sharper',
    q15: '1.5 Mbit · weak link',
  },

  audio: {
    title: 'Audio',
    on: 'Audio on',
    off: 'Audio',
    toggleTitle: 'Sound from the phone — Opus, ~64 kbit/s',
    gain: 'Playback volume in the browser (the phone’s own volume is the ⊖ ⊕ keys)',
    tone: 'beep',
    toneTitle: 'Play 440 Hz straight into the browser output — to tell “the browser plays elsewhere” from “the decoder is stuck”',
    decoded: (n: number) => `decoded ${n}/s`,
    ctx: (state: string) => `ctx=${state}`,
    gainValue: (v: string) => `gain ${v}`,
    fineHere: 'decoding and playing — if it is quiet, check the output device on your laptop',
    noPackets: 'no packets',
    starting: 'starting up (about 4 s)',
    quiet: 'quiet (the phone is not playing anything)',
    kbit: (n: string) => `${n} kbit/s`,
    noContext: 'the browser refused to open an audio context',
    noOpus: 'this browser does not decode Opus (Chrome/Edge needed)',
    decoderError: (msg: string) => `decoder: ${msg}`,
    outputError: (msg: string) => `output: ${msg}`,
    unavailable: 'audio unavailable',
    toneReport: (state: string, channels: number, chain: string) =>
      `test tone: 440 Hz straight to the output, ctx=${state}, ${channels} ch., ${chain}. If you hear nothing, check the output device and the system mixer — not the phone.`,
    chainGain: (v: string) => `channel gain ${v}`,
    chainClosed: 'channel not open',
  },

  input: {
    xiaomiSecurity: 'this phone refuses taps and keys from a computer. On Xiaomi / Redmi / POCO: Settings → Additional settings → Developer options → switch on “USB debugging (Security settings)” (it asks for a SIM card and a Mi account), then reconnect the phone',
    notDelivered: (label: string, hint: string) => `${label}: not delivered — ${hint}`,
    channelDown: 'control channel is down — reconnecting',
    reconnecting: 'reconnecting the control channel…',
    touch_down: 'touch',
    touch_move: 'move',
    touch_up: 'release',
    tap: 'tap',
    swipe: 'swipe',
    text: 'text',
    key: 'key',
    sent: (label: string) => `${label} → phone`,
    failed: (label: string) => `${label}: did not arrive`,
    channelOpen: 'input channel open',
  },

  logs: {
    title: 'Phone log',
    needSession: 'open a session to read its log',
    turnOn: 'Show log',
    turnOff: 'Hide log',
    toggleTitle: 'Reading the log costs the same USB link as taps and video — it is off until you ask',
    live: 'live',
    closed: 'closed',
    packagePlaceholder: 'package (empty = the whole phone)',
    packageLabel: 'Package filter',
    matchPlaceholder: 'contains…',
    matchLabel: 'Text filter',
    apply: 'Apply',
    clear: 'Clear',
    follow: 'follow',
    waiting: 'waiting for the first lines…',
    started: (pkg: string | null, match: string | null, replaying: number | null) =>
      `--- log started · ${pkg ? `package ${pkg}` : 'whole phone'}${match ? ` · contains “${match}”` : ''}${replaying ? ` · replaying up to ${replaying} lines the phone still holds` : ''}`,
    hint: 'Your own taps and keys are shown in colour (▸). Crashes and ANRs are always kept, whoever wrote them.',
    offHint: 'The log is off. Turn it on to see what the app does while you drive the phone — it replays what the phone still holds.',
  },

  videos: {
    results: 'What it brought',
    viewsWord: 'views',
    likesWord: 'likes',
    postsAccounts: (posts: number, accounts: number) => `${posts} posts from ${accounts} accounts`,
    lastPosted: (when: string) => `last ${when}`,
    history: (n: number) => `history · ${n}`,
    bestPost: (views: string) => `best: ${views}`,
    neverPosted: 'never posted yet',
    orderNew: 'Newest',
    orderBest: 'What shoots',
    orderNever: (n: number) => `Never posted · ${n}`,
    notReadYet: 'no numbers yet',
    openPost: 'open the post',
    noLink: 'no link',
    directionFor: 'Direction for the upload…',
    pickDirectionFirst: 'Pick a direction first — a video belongs to one direction and goes only to its phones.',
    noDirectionsYou: 'You have no direction yet — an admin adds one on the Users page',
    direction: 'Direction',
    setDirection: 'set direction…',
    moveDirection: 'change…',
    noDirection: 'no direction — goes nowhere',
    allDirections: (n: number) => `All · ${n}`,
    withoutDirection: (n: number) => `No direction · ${n}`,
    title: 'Videos',
    sub: 'Upload once, then push to any set of machines and phones',
    upload: 'Upload video',
    dropTitle: 'Drop .mp4 / .mov / .webm here, or click to choose',
    dropBody: 'Large files go up in 8 MB pieces and resume after a dropped connection.',
    hashing: 'computing checksum…',
    uploaded: (name: string) => `${name} uploaded`,
    rejected: 'the server rejected the file (checksum mismatch)',
    deleted: (name: string) => `${name} deleted`,
    readError: (msg: string) => `could not read the video list: ${msg}`,
    none: 'no videos yet',
    file: 'file',
    size: 'size',
    state: 'state',
    uploadedBy: 'uploaded',
    titleLabel: 'Video title',
    renameTitle: 'Click to give the video a title; it is shown instead of the filename. Empty clears it.',
    renamed: (name: string) => `now called ${name}`,
    status: { uploading: 'uploading', ready: 'ready', error: 'error' } as Record<string, string>,
    distribute: 'Distribute',
    download: 'Download',
    delete: 'Delete',
    deleteTitle: (name: string) => `Delete ${name}?`,
    deleteBody: 'The file is removed from the server. Copies already pushed to phones stay there; a distribution that is still running blocks the deletion.',
  },

  distribute: {
    otherDirection: 'a phone of another direction — a video goes only to its own direction’s phones',
    noDirectionPhone: 'no direction',
    title: (name: string) => `Distribute ${name}`,
    body: 'Each machine downloads the file once and copies it into every selected phone’s gallery (Movies/TikTokHub). Publishing to TikTok is then done on the phone, from its live screen.',
    loading: 'looking up machines…',
    phonesOnline: (online: number, total: number) => `${online} of ${total} phones online`,
    allOnline: 'all online phones',
    someOf: (n: number, total: number) => `${n} of ${total} phones`,
    send: (machines: number) => (machines === 1 ? 'Send to 1 machine' : `Send to ${machines} machines`),
    sending: 'Sending…',
    started: (machines: number) => `Distribution started on ${machines} machine(s)`,
    uniquify: 'Uniquify per phone',
    uniquifyHint: 'each phone gets its own variant: 25–30 % cut top or bottom, a background and a translucent overlay clip in its place',
    capacity: (phones: number, capacity: number) => `${phones} phones · sources allow ${capacity}`,
    notEnoughSources: (needed: number, available: number) =>
      `${needed} phones need ${needed} distinct variants but the sources allow only ${available} — upload more overlays or backgrounds, or pick fewer phones`,
    delaysHint: 'Pushes are staggered: up to 5 min between machines, up to 10 min between phones on one machine.',
    card: 'Title card',
    cardHint: 'a still image (your logo) shown for a few seconds on every variant',
    cardNone: 'no card',
    cardUpload: 'Upload a card…',
    cardPlacement: { start: 'at the start', end: 'at the end', both: 'start and end' } as Record<string, string>,
    cardSeconds: 'seconds',
    publish: 'Post to TikTok after delivery',
    publishWith: (name: string) => `active scenario: ${name}`,
    publishNone: 'no active scenario — nothing will be posted; record one from a live session',
    cardRetention: 'A card at the start costs views: the first 1–2 seconds decide whether the clip is skipped. Prefer the end, or keep an intro under 2 s.',
  },

  distributions: {
    title: 'Distributions',
    sub: 'Every push of a video to machines and phones, newest first',
    readError: (msg: string) => `could not read distributions: ${msg}`,
    none: 'nothing has been distributed yet',
    video: 'video',
    machines: 'machines',
    phones: 'phones',
    state: 'state',
    started: 'started',
    doneOf: (done: number, total: number) => `${done}/${total} done`,
    failed: (n: number) => `${n} failed`,
    status: { running: 'running', done: 'done', partial: 'partial', error: 'failed', cancelled: 'stopped' } as Record<string, string>,
    target: { pending: 'waiting', pushing: 'pushing', done: 'on the phone', error: 'failed', cancelled: 'cancelled' } as Record<string, string>,
    uniquify: 'Uniquification',
    variant: { queued: 'variant queued', rendering: 'rendering', done: 'variant ready', error: 'variant failed', none: '', cancelled: 'render cancelled' } as Record<string, string>,
    attempt: (n: number) => `attempt ${n}`,
    rendering: (n: number) => `${n} rendering`,
    variantsFailed: (n: number) => `${n} failed after 3 attempts`,
    publishTitle: 'Posting',
    publishState: { none: '', waiting: 'posting waits', running: 'posting…', done: 'posted', error: 'posting failed', skipped: 'not posted', cancelled: 'posting cancelled', by_hand: 'waiting for Claude to post', unconfirmed: 'posted? not seen yet', not_found: 'not on the profile' } as Record<string, string>,
    unconfirmedTitle: 'The posting scenario ran its taps, but no profile reading has seen the post yet — it reads posted once the profile shows it (a reading comes about an hour after a post).',
    byHandTitle: 'Launched through Claude: the video is on the phone and Claude posts it itself, looking at the screen — no scenario taps blind. It reads posted once Claude recorded the link.',
    byClaude: 'posted by Claude, by eye',
    openPost: 'open the post ↗',
    publishIn: (s: number) => (s >= 60 ? `posting in ${Math.ceil(s / 60)} min` : `posting in ${s} s`),
    publishFailedAt: (step: number) => `failed at step ${step + 1}`,
    publishScreenshot: 'screen at the failure',
    publishRetry: 'Post again',
    release: 'Release',
    released: (name: string) => `${name}: released — the pushes start`,
    heldNote: 'held — look at the variants, then release',
    viewVariant: 'view variant',
    retryPush: 'Deliver again',
    pushRetried: (device: string, n: number, of: number) => `delivering to ${device} again (try ${n} of ${of})`,
    pushTries: (n: number, of: number) => `delivered again ${n} of ${of}`,
    pushTriesUsed: 'tries used — look at the phone, then a new post',
    publishRetried: (device: string) => `posting started again on ${device}`,
    publishLog: 'posting log',
    releaseIn: (s: number) => (s >= 60 ? `push in ${Math.ceil(s / 60)} min` : `push in ${s} s`),
    stop: 'Stop',
    stopTitle: (name: string) => `Stop the distribution of ${name}?`,
    stopBody: 'Queued renders are dropped, a render in flight is killed, nothing more goes to the phones. What already landed stays. This cannot be undone.',
    stopped: (name: string) => `distribution of ${name} stopped`,
    delete: 'Delete',
    deleteTitle: (name: string) => `Remove the distribution of ${name} from the list?`,
    deleteBody: 'Only the record goes. The video and its variants stay.',
    deleted: (name: string) => `distribution of ${name} deleted`,
    back: '← All distributions',
    showLog: 'machine log',
    hideLog: 'hide log',
    emptyLog: '(the machine has printed nothing yet)',
  },

  scenarios: {
    title: 'Posting scenarios',
    sub: 'What a person did once in a live session; every phone repeats it after its video lands',
    rule: 'Record from the Workspace: open a phone, go to the Home screen (or into TikTok), press “Record scenario”, do the whole posting flow, stop, name it. The replay starts from the screen the recording started on. Exactly one scenario is active — that one runs in every distribution. Do not type a real caption while recording: it would be posted verbatim on every phone. Type a placeholder word and mark that step as the caption; a post without a caption skips it.',
    readError: (msg: string) => `could not read scenarios: ${msg}`,
    none: 'no scenarios yet — record one from a live session',
    active: 'active',
    activate: 'Make active',
    deactivate: 'Deactivate',
    steps: (n: number) => `${n} ${n === 1 ? 'step' : 'steps'}`,
    recordedOn: (d: string) => `recorded on ${d}`,
    tryOn: 'Try on phone',
    tryPick: 'choose a phone…',
    tryStarted: (device: string) => `scenario started on ${device}`,
    tryLog: 'run log',
    rename: 'Rename',
    delete: 'Delete',
    deleteTitle: (name: string) => `Delete scenario ${name}?`,
    deleteBody: 'Distributions already running keep their copy of it.',
    deleted: (name: string) => `${name} deleted`,
    saved: (name: string) => `${name} saved`,
    hasText: 'contains typed text',
    captionStep: 'caption step',
    markCaption: 'this is the caption',
    unmarkCaption: 'not the caption',
    captionHint: 'Mark the text step where the caption is typed: a post pastes its own text there instead of the recorded words. Without it a post with a caption cannot launch; a post without one skips the step.',
    captionMarked: 'caption step set',
    stepKinds: { tap: 'tap', swipe: 'swipe', text: 'text', key: 'key', wait: 'wait', launch: 'launch', stop: 'stop' } as Record<string, string>,
  },

  posts: {
    notYoursEditor: 'This post was created by somebody else: only its owner or an admin can change it.',
    title: 'Posts',
    sub: 'A video, an animated banner, the caption, the uniquification, the phones — prepared once, launched once',
    none: 'no posts yet — pick a ready video to start one',
    wizard: {
      title: 'Post steps',
      video: 'Video and caption',
      optional: 'optional',
      done: 'done',
      needVideo: 'pick a video',
      needText: 'write the caption',
      needPhones: 'pick the phones',
      needCheck: 'press “Check”',
    },
    newPick: 'New post from video…',
    video: 'Video',
    text: 'Caption',
    textPlaceholder: 'Optional. The text of the post: hashtags, emoji, any language…',
    phones: 'Phones',
    phonesSub: 'Every phone gets its own uniquified variant with the banner, then the active scenario posts it with this caption',
    phone: 'phone',
    stage: 'stage',
    banner: 'Banner',
    bannerNone: 'no banner',
    bannerUpload: 'Upload a banner…',
    bannerHint: '.webm / .mov / .gif with transparency, laid over the finished frame — strip included, so it is never cut. It is fitted into its box with its proportions kept — never stretched, never cropped — and anchored to the top or the bottom edge.',
    box: { width: 'width', height: 'height', anchor: 'anchor', margin: 'margin from the edge', top: 'top', bottom: 'bottom' } as Record<string, string>,
    strip: {
      title: 'Uniquification',
      sub: 'Every phone gets a strip cut from the frame and filled with a background plus a semi-transparent overlay clip. Each phone draws its own values inside these ranges.',
      side: 'cut side',
      sides: { top: 'top', bottom: 'bottom', random: 'random per phone' } as Record<string, string>,
      height: 'strip height',
      opacity: 'overlay opacity',
      min: 'min',
      max: 'max',
      rangeHint: 'Equal min and max fix the value on every phone. The preview shows the max height and opacity, and the top when the side is random.',
      exampleOverlay: 'preview overlay',
      exampleBackground: 'preview background',
      exampleDefault: (name: string) => `oldest uploaded (${name})`,
      exampleNone: 'none uploaded yet',
      exampleHint: 'Only for the preview: the phones draw their own overlay + background pair from the whole catalogue.',
      sound: 'Sound',
      pitch: 'pitch shift',
      music: 'music volume',
      soundHint: 'The pitch moves up or down by a random amount in the range, the length stays. Music is a random quiet slice of a random uploaded track; 0–0 means none.',
      reset: 'Back to the defaults',
    },
    cardSub: 'A still image (your logo) shown for a few seconds at the start and/or the end of every variant. Upload cards on the Sources page.',
    preview: 'Preview',
    previewHint: 'Exact frame: what the final file will hold, with the strip at its max height from the preview sources. In motion: the strip and the banner animating over the video in the browser.',
    previewFrame: 'exact frame',
    previewMotion: 'in motion',
    previewAt: 'at',
    previewLoading: 'rendering the frame…',
    previewNoVideo: 'pick a video to see the preview',
    previewCaption: 'caption as TikTok will show it (not part of the file)',
    overlayTitle: 'Text on the video',
    overlaySub: 'Burned into the picture on every phone. Separate from the caption.',
    overlayNone: 'no text on the video',
    overlayOn: 'put text on the video',
    overlayText: 'Text',
    overlayFromCaption: 'Use the caption',
    overlayBox: { nx: 'horizontal', ny: 'vertical', size: 'size', width: 'max width' } as Record<string, string>,
    overlayColor: 'text colour',
    overlayBackground: 'backing',
    overlayBackgroundOpacity: 'backing opacity',
    editTitle: 'Post draft',
    editSub: 'Saved as you edit; nothing is sent until Launch',
    save: { saved: 'saved', saving: 'saving…', dirty: 'unsaved' } as Record<string, string>,
    launchCard: 'Launch',
    check: 'Check',
    launch: 'Launch',
    launchTitle: (n: number) => `Post to ${n} phones?`,
    launchHeld: 'Launch, look first',
    launchHeldBody: 'The variants render and wait: open each phone\'s variant on the distribution, then Release — nothing is pushed before that.',
    launchBody: 'The draft is fixed as it stands. Each phone gets its variant, then the active scenario posts it with the caption. This happens once; a failed phone can be posted again by hand.',
    launched: (machines: number) => `Post launched on ${machines} machine(s)`,
    launchedTitle: 'Launched post',
    readyToLaunch: (n: number, scenario: string) => `Ready: ${n} phones, scenario “${scenario}”.`,
    openDistribution: 'Open the distribution',
    status: { draft: 'draft', launched: 'launched' } as Record<string, string>,
    stages: { rendering: 'rendering', ready: 'ready', pushing: 'pushing', waiting: 'on the phone, pausing before posting', unconfirmed: 'not seen yet', publishing: 'publishing', done: 'done', error: 'error', cancelled: 'stopped', by_hand: 'waiting for Claude', not_posted: 'not posted' } as Record<string, string>,
    failedAt: { rendering: 'rendering failed', pushing: 'delivery failed', publishing: 'posting failed' } as Record<string, string>,
    failedCount: (n: number) => `${n} failed`,
    problems: {
      post_two_sites: 'the post links two different sites — one account links one site only; keep one',
      account_other_site: 'these accounts already link another site — one account links one site only',
      phone_unreachable: 'the machine cannot reach these phones (offline or adb unauthorized) — leave them out, or accept “Allow USB debugging” on the phone',
      video_no_direction: 'the video has no direction — set it on the Videos page',
      phone_other_direction: 'phones of another direction — a video goes only to its own direction’s phones',
      account_banned: 'the account is banned — it does not post',
      account_limited: 'the account is limited — it does not post until it is active again',
      daily_limit: 'the account has posted its limit for 24 hours (3 posts, 1 while warming)',
      too_soon: 'the account posted less than 2 hours ago',
      no_directions: 'you have no direction yet — an admin adds one on the Users page',
      not_your_direction: 'this video is in a direction that is not yours',
      video_not_ready: 'the video is missing or has not finished uploading',
      banner_not_ready: 'the banner is missing or has not finished uploading',
      no_targets: 'choose at least one machine or phone',
      no_devices: 'none of the chosen phones is online',
      not_enough_sources: 'too few overlays × backgrounds for that many phones — upload more sources or pick fewer phones',
      no_scenario: 'no posting scenario is active',
      no_caption_step: 'the active scenario has no step marked as the caption (Scenarios → “this is the caption”)',
      hub_offline: 'a chosen machine is offline',
      device_not_on_hub: 'a chosen phone is not on that machine',
    } as Record<string, string>,
    delete: 'Delete',
    deleteTitle: 'Delete this post?',
    deleteBody: 'A launched post keeps its distribution; only the post record is removed.',
    deleted: 'post deleted',
  },

  sources: {
    groupVariant: 'Uniquification — every phone gets its own variant of the video',
    groupPost: 'Post design — chosen per post or distribution',
    title: 'Uniquification sources',
    sub: 'What is mixed into each phone’s variant: overlays and backgrounds for the strip, music under the sound, title cards to choose from',
    rule: 'A variant = one overlay clip on one background (the cut side and the opacity are random extras). Every phone in one distribution gets a different pair; the next video starts from the whole catalogue again. Capacity is how many phones one distribution can serve. Music does not count: a random quiet slice of a random track goes under every variant automatically.',
    capacity: (n: number) => `enough for ${n} phones`,
    readError: (msg: string) => `could not read the sources: ${msg}`,
    kind: { overlay: 'Overlay clips', background: 'Backgrounds', music: 'Music', card: 'Title cards', banner: 'Banners' } as Record<string, string>,
    kindHint: {
      overlay: 'short .mp4 / .mov / .webm, looped and laid over the strip at the opacity the post sets (55–75 % by default)',
      background: '.jpg / .png, filling the cut strip under the overlay',
      music: '.mp3 / .m4a / .wav — a random slice at 2–5 % volume, faded in and out, under every variant; use royalty-free tracks',
      card: '.jpg / .png, your logo on black; chosen in the Distribute dialog or the post editor',
      banner: '.webm / .mov / .gif with transparency — an animated clip a post lays over the frame; placed and sized in the post editor',
    } as Record<string, string>,
    none: { overlay: 'no overlay clips yet', background: 'no backgrounds yet', music: 'no music yet — variants keep only the original sound', card: 'no title cards yet', banner: 'no banners yet' } as Record<string, string>,
    upload: 'Upload',
    files: (n: number) => `${n} ${n === 1 ? 'file' : 'files'}`,
    uploaded: (name: string) => `${name} uploaded`,
    delete: 'Delete',
    deleted: (name: string) => `${name} deleted`,
    deleteTitle: (name: string) => `Delete ${name}?`,
    deleteBody: 'Variants already rendered keep it; future distributions will not use it and the capacity drops.',
  },

  account: {
    label: 'TikTok account on this phone',
    placeholder: '@account',
    none: 'set TikTok account',
    editTitle: 'Which TikTok account is signed in on this phone. A label only — one account may live on one phone.',
    set: (account: string, phone: string) => `${account} is now on ${phone}`,
    cleared: (phone: string) => `account label removed from ${phone}`,
    inUse: (account: string, device: string) => `${account} is already on ${device} — one account lives on one phone`,
  },

  simNumber: {
    label: 'Phone number of the SIM in this phone',
    placeholder: '+380 67 123 45 67',
    none: 'add number',
    editTitle: 'The number of the SIM in this phone, seen at a glance. One number lives on one phone.',
    set: (num: string, phone: string) => `${num} is now on ${phone}`,
    cleared: (phone: string) => `number removed from ${phone}`,
    inUse: (num: string, device: string) => `${num} is already on ${device} — one SIM, one phone`,
  },

  phoneName: {
    label: 'Phone name',
    editTitle: 'Click to name this phone; the name is shown instead of the model everywhere. Empty puts the model back.',
    set: (name: string, model: string) => `${model} is now called ${name}`,
    cleared: (model: string) => `${model} is back to its model name`,
  },

  accountPage: {
    site: 'Site',
    siteNone: 'not set yet — its first post with a link sets it',
    siteChange: 'change',
    siteSave: 'Save',
    siteCancel: 'Cancel',
    siteBy: (by: string) => `set by ${by}`,
    siteConfirm: (from: string, to: string) => `This account already links ${from}. Its posts out there still point there. Make it ${to || 'none'} anyway? One account links one site only.`,
    unknown: 'No such account — the link may be old.',
    openPhone: 'Open the phone',
    onPhone: (phone: string) => `on phone ${phone}`,
    profile: 'TikTok profile',
  },
  direction: {
    unknown: 'No such direction.',
    tabs: { stats: 'Statistics', accounts: 'Accounts', posts: 'Publications', links: 'Links', phones: 'Phones' } as Record<string, string>,
  },
  trend: {
    dayChange: (d: string) => `that day ${d}`,
    lastDay: (day: string) => `${day}, that day:`,
    bestDay: (day: string, v: string) => `best day — ${day}: ${v}`,
    firstReading: (day: string) => `first reading — ${day}; the line grows day by day, TikTok does not show past days`,
    notRead: 'not read yet',
    since: (day: string) => `since ${day}`,
    overDays: (n: number) => `over ${n} days`,
  },
  sections: {
    directions: 'Directions',
    accounts: 'All accounts',
    publications: 'All publications',
    links: 'Links',
    posts: 'Launch posts',
    distributions: 'Distributions',
    videos: 'Videos',
    machines: 'Machines',
    sources: 'Sources',
    scenarios: 'Scenarios',
    users: 'Users',
    myClaude: 'My Claude',
    keys: 'Keys',
  },
  dirs: {
    title: 'Directions',
    sub: 'what we promote — and which phones work for what',
    rules: [
      'A direction is what we promote: games, dramas and so on. The dashboard, reports and content are split by direction.',
      'Each phone belongs to one direction for ever, with every account on it: it posts only that direction’s content.',
      'Marketing does not use a phone without a direction: its stats are not read and it is left out of reports. Playing on it and taking sessions work as always.',
      'A direction can be renamed or recoloured any time; a phone’s binding cannot be changed.',
    ],
    newName: 'Name of a new direction, e.g. Casino',
    color: 'Colour',
    add: 'Add direction',
    edit: 'Rename / colour',
    save: 'Save',
    cancel: 'Cancel',
    counts: (phones: number, accounts: number) => `phones: ${phones} · accounts: ${accounts}`,
    noPhones: 'No phones yet — bind them below, under “Without a direction”.',
    noAccounts: 'no accounts registered',
    status: { online: 'online', offline: 'offline', busy: 'busy' } as Record<string, string>,
    unboundTitle: (n: number) => `Without a direction · ${n}`,
    allBound: 'Every phone has a direction.',
    unboundSub: 'marketing does not use these phones until an admin binds them to a direction',
    unboundSubAdmin: 'tick phones, pick a direction and bind — all at once',
    pickAll: 'Tick all',
    pickNone: 'Untick all',
    toDirection: 'to direction…',
    bind: (n: number) => `Bind (${n})`,
    confirmTitle: (n: number, dir: string) => `Bind ${n} phone(s) to “${dir}” for ever`,
    confirm: 'Bind for ever',
  },
  marketing: {
    /** the built-in directions in the page's language, while an admin has not renamed them */
    builtin: { games: 'Games', dramas: 'Dramas' } as Record<string, string>,
    title: 'Accounts',
    sub: 'accounts living on the phones, by direction — who is alive, limited or banned',
    rules: 'A phone belongs to one direction for ever (an admin binds it on the Directions page). An account lives on one phone for ever and is never deleted — one that is gone is marked logged out. No passwords anywhere.',
    allDirections: 'All directions',
    noBoundPhones: 'No phone has a direction yet — an admin binds them on the Directions page first.',
    emailPlaceholder: 'the email it signs in with (name@gmail.com)',
    handlePlaceholder: '@handle (optional)',
    handleOnly: '@handle or the profile link',
    addHandle: '+ handle',
    phone: 'Phone',
    pickPhone: 'phone…',
    stateLabel: 'State',
    add: 'Add account',
    state: { warming: 'warming up', active: 'active', limited: 'limited', banned: 'banned', logged_out: 'logged out' } as Record<string, string>,
    changeState: 'change state…',
    reasonRequired: 'what was seen — required',
    reasonOptional: 'what was seen (optional)',
    save: 'Save',
    history: (n: number) => `history · ${n}`,
    groupSub: (phones: number, accounts: number) => `${phones} phone${phones === 1 ? '' : 's'} · ${accounts} account${accounts === 1 ? '' : 's'}`,
    noPhonesInDirection: 'no phones in this direction yet',
    noAccounts: 'no accounts registered on this phone',
    noDirection: 'no direction',
    noDirectionTitle: 'not bound to a direction yet — marketing cannot use it until an admin binds it',
    bindButton: 'Set direction',
    bindTitle: (phone: string) => `Bind ${phone} to a direction`,
    bindPick: 'Pick a direction',
    bindConfirm: (_dir: string) => 'Bind for ever',
    bindForever: 'This is permanent: the phone and every account on it will belong to this direction for ever. Only a database edit could change it.',
    bindOnlyMarketing: 'It limits marketing only — the phone still takes sessions, and Claude can still play games on it.',
    directionsTitle: 'Directions',
    directionsSub: 'admins only: add a direction or see what each holds',
    directionStats: (phones: number, accounts: number) => `${phones} phones · ${accounts} accounts`,
    newId: 'id, e.g. apps',
    newName: 'name',
    color: 'colour',
    preview: 'preview',
    addDirection: 'Add direction',
  },
  publications: {
    title: 'Publications',
    sub: 'every post on every phone — Ferma\'s own postings appear here by themselves; posts made by hand or by Claude are recorded over MCP',
    all: 'All',
    noLink: 'No post link',
    noAccount: 'Account unknown',
    summary: (n: number, noLink: number, noAccount: number) => `${n} post${n === 1 ? '' : 's'} · ${noLink} without a link · ${noAccount} with the account unknown`,
    empty: 'Nothing here yet. Postings that finish appear within a minute.',
    day: (n: number) => `${n} post${n === 1 ? '' : 's'}`,
    video: 'video',
    text: 'text',
    source: { ferma: 'Ferma posting', recorded: 'recorded', profile: 'found on the profile' } as Record<string, string>,
    accountBy: { phone: 'the only account on this phone', link: 'from the post link', person: 'set by a person' } as Record<string, string>,
    pickAccount: 'Which account?',
    noAccounts: 'no account registered on this phone',
    addLink: 'Add the post link',
    linkPlaceholder: 'https://www.tiktok.com/@handle/video/…',
    open: 'Open the post',
    save: 'Save',
    cancel: 'Cancel',
    onWords: 'on the video',
    phoneGone: 'phone no longer connected',
    noTarget: 'No target',
    onlyLink: (name: string) => `Only posts carrying ${name}.`,
    showAll: 'Show all',
    target: 'Link',
    targetFromText: 'read from the post',
    targetByPerson: 'set by a person',
    targetReset: 'read from the post',
    pickTarget: 'Which link is in the post?',
    noTargets: 'the link list is empty — an admin adds links on the Links page',
  },

  dash: {
    phonesCount: (n: number) => `on ${n} phones`,
    title: 'Overview',
    sub: 'everything the farm is doing, and what the posts brought',
    updated: (at: string) => `updated ${at}`,
    periods: { 1: 'Today', 7: '7 days', 30: '30 days', 90: '90 days', 365: 'Year' } as Record<number, string>,
    daysN: (n: number) => `${n} days`,
    customDates: 'Pick dates',
    from: 'from',
    to: 'to',
    accountsTrendsTitle: (n: number) => `Accounts day by day · ${n}`,
    accountsTrendsSub: 'each account: followers, post views and likes every evening; the name opens the account',
    postsInPeriod: (n: number) => `posts in the period: ${n}`,
    allDirections: 'all directions',
    noDirection: 'phones without a direction',
    allPhones: 'all phones',
    phonePage: (name: string) => `Phone ${name}`,
    backToAll: 'Back to the whole farm',
    openPhone: 'Open the phone',
    nowTitle: 'Right now',
    nowSub: 'the whole farm, whatever the filter',
    phonesTile: 'Phones',
    phoneStates: { free: 'free', posting: 'posting', held: 'held by a person / Claude', reading: 'reading stats', offline: 'offline', trouble: 'plugged in, not usable' } as Record<string, string>,
    machinesTile: 'Machines',
    machinesOnline: (on: number, all: number) => `${on} of ${all} online`,
    distributionsTile: 'Distributions',
    distRunning: (n: number) => `${n} running`,
    distFinished: (done: number, partial: number, err: number) => `last 24 h: ${done} done · ${partial} partly · ${err} failed`,
    jobsTile: 'Jobs on phones',
    jobsLine: (run: number, queued: number) => `${run} running · ${queued} waiting`,
    jobsDay: (done: number, failed: number) => `last 24 h: ${done} done · ${failed} failed`,
    passTile: 'Reading profiles',
    passOn: 'schedule on',
    passOff: 'schedule off',
    passRunning: (who: string) => `reading ${who}`,
    passDay: (n: number) => `${n} readings in 24 h`,
    heldTitle: 'Who holds which phone',
    heldFor: (min: number) => `${min} min`,
    heldPosting: 'auto-posting',
    heldReading: 'reading stats — free to take',
    runningTitle: 'Distributions in flight',
    pushed: (done: number, all: number) => `pushed ${done}/${all}`,
    rendered: (done: number, all: number) => `uniquified ${done}/${all}`,
    published: (done: number, all: number, failed: number) => `posted ${done}/${all}${failed ? ` · ${failed} failed` : ''}`,
    nothingRunning: 'Nothing is being distributed right now.',
    nobodyHolds: 'Nobody holds a phone right now.',
    alertsTitle: 'Needs a person',
    alertsNone: 'Nothing needs attention.',
    more: (n: number) => `and ${n} more`,
    alert: {
      not_recommended: (n) => `TikTok keeps out of the For You feed: ${n} post(s) — no organic reach`,
      hubs_offline: (n: number) => `${n} machine(s) are not connected`,
      phones_offline: (n: number) => `${n} phone(s) offline`,
      phones_trouble: (n: number) => `${n} phone(s) plugged in but not usable (adb)`,
      battery_low: (n: number) => `${n} phone(s) almost flat and not charging`,
      battery_weak_power: (n: number) => `${n} phone(s) on a cable that does not charge them`,
      hot: (n: number) => `${n} phone(s) running hot`,
      accounts_banned: (n: number) => `${n} account(s) banned`,
      accounts_limited: (n: number) => `${n} account(s) limited`,
      profile_trouble: (n: number) => `${n} account(s): the last profile reading found a problem`,
      distributions_failed: (n: number) => `${n} distribution(s) in 24 h did not post everywhere`,
      jobs_failed: (n: number) => `${n} job(s) on phones failed in 24 h`,
      doubtful: (n: number) => `${n} account(s) have posts not matched for sure — a person decides`,
      not_found: (n: number) => `${n} account(s): Ferma's posts are missing from the profile`,
      phones_unbound: (n: number) => `${n} phone(s) without a direction — not read, not counted`,
      phones_no_account: (n: number) => `${n} phone(s) with a direction but no account`,
      accounts_no_email: (n: number) => `${n} account(s) without an email`,
      posts_no_link: (n: number) => `${n} post(s) carry no target link`,
      posts_no_account: (n: number) => `${n} post(s) without an account`,
      links_unlisted: (n: number) => `${n} address(es) in posts are not on the links list`,
      accounts_two_sites: (n: number) => `${n} account(s) already link two different sites — the one thing that must not happen`,
      links_shared: (n: number) => `${n} link(s) carried by many phones — a thread that ties accounts`,
      pass_off: () => 'The profile reading schedule is off — numbers update only by hand',
    } as Record<string, (n: number) => string>,
    dirPeriodTitle: (period: string) => `By direction · ${period}`,
    dirPeriodSub: 'posts that went out in the period and what views, likes and followers grew in it, against the stretch before; accounts in trouble as of now',
    lineSince: (day: string, now: string) => `${now} now · read since ${day}, a line after 3 days`,
    lineFrom: (day: string) => `since ${day}`,
    yesterdayTitle: (day: string) => `Yesterday, ${day}`,
    yesterdaySub: 'a Kyiv day; views, likes and followers are what was gained that day',
    periodTitle: 'By direction',
    periodSub: (from: string) => `since ${from}, against the same stretch before`,
    unbound: 'Without a direction',
    posts: 'posts',
    views: 'views',
    likes: 'likes',
    comments: 'comments',
    shares: 'shares',
    followers: 'followers',
    gained: 'gained',
    accountStatsTitle: 'Account statistics',
    profileViews: 'Profile views',
    hintFollowers: 'followers at the end of each day',
    hintViews: 'views of every post the profile shows, added up — at the end of each day',
    hintLikes: 'the account’s total likes (the profile’s number) at the end of each day',
    hintPosts: 'posts that went out each day',
    hintProfileViews: 'profile visits so far — at the end of each day',
    totalNow: (v: string) => `${v} in all now`,
    inPeriod: (v: string) => `${v} in the period`,
    postsInPeriodShort: 'went out in the period',
    kpiTitle: 'The period at a glance',
    clicks: 'Link clicks',
    clicksNotYet: 'not collected yet',
    accTableTitle: (n: number) => `Accounts · ${n}`,
    accTableSub: 'every account in one table — click a column head to sort; the name opens the account',
    colAccount: 'Account',
    colFollowersLine: 'Followers by day',
    colFollowers: 'Followers',
    colFollowersGained: 'In the period',
    colViews: 'Views',
    colViewsGained: 'In the period',
    colPosts: 'Posts',
    colRead: 'Read',
    numbersNotYet: 'views, likes and followers not read off the phones yet',
    firstReadInPeriod: 'first reading',
    notRecommended: 'not in For You',
    notRecommendedLong: 'TikTok keeps this post out of recommendations (For You) — no organic reach',
    foundPost: 'A post from the profile (not through Ferma)',
    trendsTitle: 'Day by day',
    trendsSub: (n: number) => `how each direction grew over ${n} days — where it stood each evening; a gap is a day not read yet`,
    viewsAll: 'Post views',
    likesAll: 'Account likes',
    statsTitle: 'Direction statistics',
    fromProfile: 'from the profile',
    notRead: 'not read yet',
    best: 'Best post',
    noBest: 'no post grew yet',
    trouble: 'Accounts in trouble',
    noTrouble: 'all accounts fine',
    phonesAccounts: (p: number, on: number, a: number) => `${p} phones (${on} online) · ${a} accounts`,
    followersTotal: (n: string) => `${n} followers in all`,
    vsBefore: (sign: string, pct: string) => `${sign}${pct} vs before`,
    sameAsBefore: 'same as before',
    newSinceBefore: 'nothing before',
    perDay: 'views gained per day',
    postsPerDay: 'posts per day',
    noReadings: 'No profile has been read yet, so views and followers are unknown — they appear after the first reading (Accounts → "Read the profile now", or the schedule).',
    phonesTitle: 'Phones and accounts',
    phonesSub: 'posts, views and followers over 7 days',
    col: { phone: 'Phone', direction: 'Direction', state: 'Now', accounts: 'Accounts', followers: 'Followers', posts7: 'Posts 7 d', views7: 'Views 7 d', lastPost: 'Last post', alerts: 'Alerts' } as Record<string, string>,
    noAccounts: 'no account',
    never: 'never',
    phoneAlert: {
      not_recommended: 'posts kept out of For You',
      offline: 'offline', trouble: 'adb', battery_low: 'flat', battery_weak_power: 'not charging', hot: 'hot',
      unbound: 'no direction', no_account: 'no account', account_banned: 'banned', account_limited: 'limited',
      account_signed_out: 'signed out', account_other_account: 'another account', account_no_tiktok: 'no TikTok',
      account_failed: 'reading failed', account_locked: 'PIN lock',
    } as Record<string, string>,
    feedExplain: 'Every post of the chosen period, most views first. The numbers are from the last time a phone opened the post; “How it grew” shows how they changed between readings.',
    feedUnread: (n: number, all: number) => `${n} of ${all} posts have not been opened by a phone yet — no numbers for them so far. They appear after the next profile readings.`,
    postNotRead: 'No numbers yet — a phone has not opened this post.',
    accountUnknown: 'account unknown',
    carries: (links: string) => `link: ${links}`,
    hintPostViews: 'the post’s views at each reading',
    hintPostLikes: 'its likes at each reading',
    everyReading: (n: number) => `every reading (${n})`,
    feedTitle: 'Posts by result',
    feedSub: (shown: number, all: number) => all > shown ? `${shown} of ${all} in the period` : `${all} in the period`,
    feedNone: 'No posts in this period.',
    gainedInPeriod: (n: string) => `+${n} views in the period`,
    found: 'found in the profile',
    openPost: 'Open on TikTok',
    growth: 'How it grew',
    growthNone: 'Read only once so far — the line appears after the next reading.',
    noPicture: 'no picture',
    battery: (n: number) => `${n}%`,
  },

  profile: {
    title: 'Reading profiles',
    sub: 'the phones open their own TikTok profile and read what posts collected — read only, at the lowest priority',
    rules: 'Read only: the profile tab, the posts, Back and Home — no likes, no typing. A phone somebody holds is skipped; anybody who takes the phone (posting, a person, Claude) stops the reading on the spot. One phone at a time; each account every 6 hours and an hour after each new post; only phones with a direction. A PIN lock is left alone.',
    autoOn: 'Schedule on',
    autoOff: 'Schedule off — readings only by hand',
    switchOn: 'Switch the schedule on',
    switchOff: 'Switch the schedule off',
    readingNow: (who: string) => `reading now: ${who}`,
    readNow: 'Read the profile now',
    started: 'Reading — about a minute or two. The result appears here.',
    never: 'not read yet',
    when: (at: string) => `read ${at}`,
    counts: (followers: string, likes: string, posts: number, views: string) => `${followers} followers · ${likes} likes · ${posts} posts read, ${views} views`,
    outcome: {
      partial: 'stopped part-way — tried again within the hour',
      ok: 'read', locked: 'the phone is locked with a PIN', busy: 'the phone was busy — skipped',
      evicted: 'stopped: somebody took the phone', no_tiktok: 'TikTok did not open', signed_out: 'TikTok is signed out',
      banned: 'TikTok shows a ban', other_account: 'another account is on the phone', failed: 'the reading failed',
    } as Record<string, string>,
    doubtful: (n: number) => `${n} post(s) not matched for sure — a person decides`,
    notFound: (n: number) => `${n} of Ferma's posts are not in the profile`,
    details: 'What was read',
    match: { sure: 'its publication', doubtful: 'doubtful', found: 'found in the profile' } as Record<string, string>,
    statNames: { views: 'views', likes: 'likes', comments: 'comments', shares: 'shares', saves: 'saves' } as Record<string, string>,
    statsAt: (at: string) => `as read ${at}`,
    aiLabel: 'TikTok marks it as AI-generated',
    phoneReading: 'reading stats — free to take',
  },

  links: {
    title: 'Links',
    sub: 'where posts send people — a site, a Telegram channel, anything — and which posts, phones and accounts carried them',
    rules: 'A post carries exactly the link listed here — no short links or redirects of ours: one domain behind every account ties them together. So clicks are seen only by the link’s owner (the site’s analytics); here you see every post that carried it. Ferma reads a post’s link from the words on the video and the caption.',
    listTitle: 'List',
    listSub: (n: number) => `links: ${n}`,
    empty: 'Nothing yet. An admin adds links above — or from those already found in posts, below.',
    urlPlaceholder: 'the address as in the post: manyplay.fun, https://t.me/…',
    namePlaceholder: 'name (optional)',
    direction: 'Direction',
    anyDirection: 'any direction',
    add: 'Add',
    carried: (pubs: number, phones: number, accounts: number) => `${pubs} post(s) · phones: ${phones} · accounts: ${accounts}`,
    notPosted: 'in no post yet',
    span: (from: string, to: string) => from === to ? from : `${from} – ${to}`,
    sharedWidely: (phones: number, at: number) => `on ${phones} phones (threshold ${at}) — one address on many accounts can tie them together`,
    otherDirection: 'posts of another direction carried it',
    retire: 'No longer promoted',
    unretire: 'Bring back',
    retiredList: (n: number) => `no longer promoted: ${n}`,
    unlistedTitle: 'Found in posts, not on the list',
    unlistedSub: 'addresses from the words on videos or captions — add the ones that are targets',
    foundIn: (n: number, last: string) => `in ${n} post(s), last ${last}`,
    addFound: 'Add to the list',
  },

  machines: {
    usb: {
      level: (pct: number) => `${pct}%`,
      charging: 'charging',
      weakShort: 'draining',
      weakCharge: 'on the cable but draining — the hub port does not keep up',
      ranFlat: (pct: number, at: string) => `ran flat (last seen at ${pct}%, ${at}) — switch it on with its button`,
      seat: (hub: string, place: number) => `hub ${hub} · place ${place}`,
      hubOnly: (hub: string) => `hub ${hub}`,
      group: (hub: string, n: number) => `Hub on computer port ${hub} · ${n} phone(s)`,
      unknownPort: 'USB port not reported yet',
      port: (path: string) => `USB ${path}`,
      portTitle: (route: string) => `where it hangs: ${route}`,
      route: (bus: string, ports: string[]) => `bus ${bus} · computer port ${ports[0]}${ports.slice(1).map((p) => ` → hub port ${p}`).join('')}`,
      since: (time: string) => `dropped at ${time}`,
      gone: 'not on USB — unplugged, or its hub has no power',
      waiting: 'still on its cable — the machine will reconnect it',
      trying: (n: number, max: number) => `the machine is reconnecting it (attempt ${n} of ${max})`,
      gaveUp: (max: number) => `the machine tried ${max} times — needs a person`,
      stateHint: (state: string) => state === 'unauthorized'
        ? 'on its cable, waiting for "Allow USB debugging" on its screen'
        : `on its cable, adb: ${state}`,
      dropped: 'Dropped',
      drops: (n: number) => `${n}× in 7 days`,
      dropLine: (at: string, how: string) => `${at} — ${how}`,
      backByHub: 'brought back by the machine',
      backUnplugged: 'cable was out, plugged back',
      backSelf: 'came back by itself',
      stillOut: 'still out',
    },
    title: 'Machines',
    rule: 'One phone = one TikTok account. Set the account on each phone here; the service refuses a second phone with the same one.',
    readError: (msg: string) => `could not read the machine list: ${msg}`,
    none: 'no machines yet',
    point: (point: string) => `site ${point}`,
    agent: (v: string) => `agent ${v}`,
    onWire: 'connected',
    offline: 'offline',
    phones: (n: number) => `${n} phone(s)`,
    onlinePhones: (n: number) => `online ${n}`,
    unlabelled: (n: number) => `${n} without an account label`,
    showPhones: 'phones',
    hide: 'hide',
    noPhones: 'no phones on this machine',
    deleteMachine: 'Delete machine',
    deletePhone: 'Delete',
    liveCannotDelete: 'Only an offline phone can be deleted — a live one re-registers on the next heartbeat',
    onlineCannotDelete: 'Stop the agent first — a connected machine registers itself again',
    deleted: (name: string) => `${name} deleted from our database`,
  },

  confirm: {
    titleMachine: (name: string) => `Delete machine ${name}?`,
    titlePhone: (name: string) => `Delete phone ${name}?`,
    ourDbOnly: 'This removes it from the Ferma Hub database only. Nothing on the machine or the phone changes.',
    cascade: (n: number) => `Its ${n} phone(s) and their session claims are deleted with it.`,
    phoneNote: 'The phone disappears from the lists in our database. If it is ever plugged into a machine again, it registers itself anew.',
    typeName: (name: string) => `Type ${name} to confirm`,
    math: (a: number, b: number, op: string) => `And solve: ${a} ${op} ${b} = ?`,
    answer: 'Answer',
    nameMismatch: 'the name does not match',
    mathWrong: 'wrong answer',
    cancel: 'Cancel',
    doDelete: 'Delete',
  },

  common: {
    dash: '—', save: 'Save', dismiss: 'Dismiss',
    adminOnly: 'Only an admin can do this',
    notYours: 'Only the owner or an admin can change or delete this',
  },

  users: {
    directions: 'Directions',
    allDirections: 'all (admin)',
    noDirections: 'none — posts nothing',
    directionsSaved: (email: string) => `Directions of ${email} saved`,
    title: 'Users',
    sub: 'Who can sign in and what they may do. Google accounts appear here after their first sign-in; password accounts are created below.',
    adminOnly: 'Only an admin can manage users.',
    readError: (msg: string) => `could not read the users: ${msg}`,
    none: 'no users yet',
    email: 'E-mail',
    name: 'Name',
    role: 'Role',
    createdAt: 'Created',
    signIn: 'Sign-in',
    password: 'password',
    googleOnly: 'Google only',
    you: 'you',
    roles: { admin: 'admin', tester: 'tester' } as Record<string, string>,
    roleHint: 'An admin manages phones, scenarios, users and everybody’s content; a tester works with their own.',
    resetPassword: 'Set a password',
    newPassword: 'New password',
    minPassword: 'at least 8 characters',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    deleteTitle: (email: string) => `Delete ${email}?`,
    deleteBody: 'The person can no longer sign in; their sessions end at once. Videos, posts and distributions they created stay.',
    addTitle: 'Add a password account',
    addSub: 'For somebody without a corporate Google account. Corporate e-mail only.',
    add: 'Create',
    created: (email: string) => `${email} created`,
    roleSaved: (email: string, role: string) => `${email} is now ${role}`,
    passwordSet: (email: string) => `password set for ${email}`,
    deleted: (email: string) => `${email} deleted`,
  },

  oauth: {
    title: 'Connect Claude to Ferma Hub',
    asks: (name: string) => `${name} asks to work in Ferma Hub as you.`,
    goesTo: 'The answer goes back to',
    loopback: 'a program on this computer (Claude Code)',
    can: 'It will be able to: see the machines and phones, take a phone in your name and drive it, upload videos and sources, create and launch posts and distributions.',
    cannot: 'It will not be able to: manage scenarios, phones, machines, users or other people’s content.',
    revokeLater: 'You can disconnect it any time on the My Claude page.',
    allow: 'Allow',
    deny: 'Deny',
    working: 'Connecting…',
    gone: 'This request has expired or was already answered. Go back to Claude and press Connect again.',
  },
}

// Deliberately no `as const`: the translations are typed against this object, and literal types
// would demand that Ukrainian also say "Phones".
export type Dict = typeof en
